import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '@/api/client'
import { Header } from '@/components/layout'
import { Card } from '@/components/ui'
import {
  PaymentAmount,
  PaymentDetails,
  SecurityBadges,
  PaymentButton,
} from '@/components/payment'

export default function Payment() {
  const { t } = useTranslation()
  const { shortCode } = useParams<{ shortCode: string }>()

  const {
    data: paymentLink,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['payment', shortCode],
    queryFn: () => api.getPaymentLink(shortCode!),
    enabled: !!shortCode,
  })

  // Check states
  const isExpired = paymentLink && new Date(paymentLink.expiresAt) < new Date()
  const isPaid = paymentLink?.status === 'paid'
  const isCancelled = paymentLink?.status === 'cancelled'

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Background with Overlay */}
      <main className="relative flex-1 flex items-center justify-center py-16 md:py-20 lg:py-12">
        {/* Background Image */}
        <div className="fixed inset-0 z-0">
          <picture className="absolute inset-0">
            <source
              media="(min-width: 1200px)"
              srcSet="https://nestrentalapartments.com/optimized-images/Paris-Londres/l-50/proyecto-londres-alta-pb-14-large.webp"
              type="image/webp"
            />
            <source
              media="(min-width: 1200px)"
              srcSet="https://nestrentalapartments.com/optimized-images/Paris-Londres/l-50/proyecto-londres-alta-pb-14-large.jpg"
              type="image/jpeg"
            />
            <source
              srcSet="https://nestrentalapartments.com/optimized-images/Paris-Londres/l-50/proyecto-londres-alta-pb-14-medium.webp"
              type="image/webp"
            />
            <source
              srcSet="https://nestrentalapartments.com/optimized-images/Paris-Londres/l-50/proyecto-londres-alta-pb-14-medium.jpg"
              type="image/jpeg"
            />
            <img
              alt=""
              loading="eager"
              className="absolute inset-0 w-full h-full object-cover"
              src="https://nestrentalapartments.com/optimized-images/Paris-Londres/l-50/proyecto-londres-alta-pb-14-large.jpg"
            />
          </picture>
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/40 via-neutral-900/20 to-neutral-900/70" />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="relative z-10 w-full max-w-lg mx-auto px-4">
            <Card variant="glass" padding="lg">
              <div className="flex items-center justify-center py-12">
                <div className="spinner" />
              </div>
            </Card>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="relative z-10 w-full max-w-lg mx-auto px-4">
            <Card variant="glass" padding="lg">
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-100 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-neutral-900 mb-2">{t('states.notFoundTitle')}</h3>
                <p className="text-neutral-600 max-w-sm mx-auto">
                  {t('states.notFoundBody')}
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* Status Messages */}
        {paymentLink && (isExpired || isPaid || isCancelled) && (
          <div className="relative z-10 w-full max-w-lg mx-auto px-4 lg:mx-0 lg:ml-auto lg:mr-16 xl:mr-24">
            <div className="slide-up">
              <Card variant="glass" padding="none" className="p-6 md:p-10">
                {isExpired ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-neutral-900 mb-2">{t('states.expiredTitle')}</h3>
                    <p className="text-neutral-600 max-w-sm mx-auto">
                      {t('states.expiredBody')}
                    </p>
                  </div>
                ) : isPaid ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-green-100 flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className="text-neutral-900 mb-2">{t('states.paidTitle')}</h3>
                    <p className="text-neutral-600 max-w-sm mx-auto">
                      {t('states.paidBody')}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-100 flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                    <h3 className="text-neutral-900 mb-2">{t('states.cancelledTitle')}</h3>
                    <p className="text-neutral-600 max-w-sm mx-auto">
                      {t('states.cancelledBody')}
                    </p>
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}

        {/* Payment Form */}
        {paymentLink && !isExpired && !isPaid && !isCancelled && (
          <div className="relative z-10 w-full max-w-lg mx-auto px-4 lg:mx-0 lg:ml-auto lg:mr-16 xl:mr-24">
            <div className="slide-up">
              <Card variant="glass" padding="none" className="p-6 md:p-10">
                <div className="space-y-6">
                  {/* Property Header */}
                  <div className="text-center pb-6 border-b border-neutral-200">
                    <p className="text-xs text-neutral-500 uppercase tracking-wider">
                      {paymentLink.propertyName}
                    </p>
                  </div>

                  {/* Details */}
                  <PaymentDetails paymentLink={paymentLink} />

                  {/* Amount */}
                  <div className="text-center pt-2">
                    <PaymentAmount
                      amount={paymentLink.amount}
                      netAmount={paymentLink.netAmount}
                      taxAmount={paymentLink.taxAmount}
                    />
                  </div>

                  {/* Pay Button */}
                  <div className="pt-2">
                    <PaymentButton shortCode={shortCode!} />
                  </div>

                  {/* Security */}
                  <SecurityBadges />
                </div>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
